import { useEffect, useMemo, useState } from 'react';

import { OrderExecutionTermsOperationKind } from 'api/types/entity/order/orderExecutionTermsEntity';
import { BigNumber } from 'bignumber.js';
import { DEFAULT_NOMINAL_VALUE } from 'modules/order/common/constants';
import { useField } from 'modules/order/common/forms/orderForm';

import { useCreateOrderContext } from '../../../../createOrder/providers/OrderProvider';

export const useTransferVolumeSection = () => {
  const { directories } = useCreateOrderContext();

  const { currencies, financialInstruments } = directories;

  const [nominalQuantity, setNominalQuantity] = useState<string>();

  const { field: nominalValueField } = useField(
    'financialInstrument.nominalValue'
  );

  const { field: quantityField } = useField('transferVolume.quantity');

  const { field: unitPriceField, helpers: unitPriceHelpers } = useField(
    'transferVolume.unitPrice'
  );
  const { field: operationKindField } = useField(
    'executionTerms.operationKind'
  );

  const { helpers: transactionAmountHelpers } = useField(
    'transferVolume.transactionAmount'
  );

  const { helpers: currencyHelpers } = useField('transferVolume.currency');

  const isDisabledPrice =
    !operationKindField?.value?.value ||
    operationKindField?.value?.value ===
      OrderExecutionTermsOperationKind.ChangeNominalHolder;

  const isRequiredPrice =
    operationKindField?.value?.value ===
      OrderExecutionTermsOperationKind.DeliveryFreeOfPayment ||
    operationKindField?.value?.value ===
      OrderExecutionTermsOperationKind.DeliveryVersusPayment;

  const isDisabledTransactionAmount = useMemo(
    () =>
      isDisabledPrice ||
      (isRequiredPrice &&
        (unitPriceField.value === undefined ||
          quantityField.value === undefined)),
    [
      isDisabledPrice,
      isRequiredPrice,
      quantityField.value,
      unitPriceField.value,
    ]
  );

  useEffect(() => {
    if (quantityField.value !== undefined) {
      const nominalValue = BigNumber(
        (nominalValueField?.value as string) || DEFAULT_NOMINAL_VALUE
      );

      setNominalQuantity(
        nominalValue.multipliedBy(quantityField.value).toFixed()
      );
    } else {
      setNominalQuantity(undefined);
    }
  }, [financialInstruments, quantityField.value, nominalValueField?.value]);

  useEffect(() => {
    if (isDisabledPrice) {
      unitPriceHelpers.setValue(undefined);
      unitPriceHelpers.setError(undefined);
      transactionAmountHelpers.setError(undefined);
      transactionAmountHelpers.setValue(undefined);
      currencyHelpers.setValue(null);
      currencyHelpers.setError(undefined);
      return;
    }

    if (!isRequiredPrice) {
      return;
    }

    // Update transaction amount when quantity or unit price changes
    if (
      unitPriceField.value !== undefined &&
      quantityField.value !== undefined
    ) {
      transactionAmountHelpers.setError(undefined);
      const bigNumber = BigNumber(unitPriceField.value);
      if (bigNumber.isNaN()) {
        transactionAmountHelpers.setValue(undefined);
        return;
      }
      transactionAmountHelpers.setValue(
        bigNumber.multipliedBy(quantityField.value).toFixed()
      );
    } else {
      transactionAmountHelpers.setValue(undefined);
    }
  }, [
    transactionAmountHelpers,
    quantityField.value,
    unitPriceField.value,
    unitPriceHelpers,
    isDisabledPrice,
    isRequiredPrice,
    currencyHelpers,
  ]);

  return {
    isDisabledPrice,
    isRequiredPrice,
    isDisabledTransactionAmount,
    currencies,
    financialInstruments,
    nominalQuantity,
  };
};
